import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { VideoExtensionRequestRecord } from '@/api/videoApi'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import CloseIcon from '@mui/icons-material/Close'
import { formatDuration, timeAgo, STATUS_CONFIG } from '@/utils/common/extensionRequestUtils'

interface ExtensionRequestDetailModalProps {
  req: VideoExtensionRequestRecord
  onClose: () => void
  // Admin-only
  isAdmin?: boolean
  actionLoading?: boolean
  onApprove?: () => void
  onReject?: (feedback: string) => void
  onViewExtended?: (videoId: string) => void | Promise<void>
}

const ExtensionRequestDetailModal: React.FC<ExtensionRequestDetailModalProps> = ({
  req,
  onClose,
  isAdmin = false,
  actionLoading = false,
  onApprove,
  onReject,
  onViewExtended,
}) => {
  const navigate = useNavigate()
  const [rejectFeedback, setRejectFeedback] = useState('')
  const [showRejectInput, setShowRejectInput] = useState(false)
  const [viewLoading, setViewLoading] = useState(false)
  const rejectInputRef = useRef<HTMLInputElement>(null)

  const cfg = STATUS_CONFIG[req.status] ?? STATUS_CONFIG.pending
  const aspectRatio = req.aspect_ratio || '16:9'
  const isPortrait = aspectRatio === '9:16' || aspectRatio === '3:4' || aspectRatio === '9:21'
  const objectFitClass = isPortrait ? 'object-contain' : 'object-cover'

  const renderVideoPreview = () => {
    if (req.video_url) {
      return (
        <video
          src={req.video_url}
          poster={req.thumbnail_url || undefined}
          controls
          playsInline
          className={`w-full h-full ${objectFitClass}`}
        >
          <track kind="captions" />
        </video>
      )
    }
    
    if (req.thumbnail_url) {
      return <img src={req.thumbnail_url} alt="thumbnail" className="w-full h-full object-cover" />
    }
    
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
        No preview available
      </div>
    )
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  useEffect(() => {
    if (showRejectInput) {
      rejectInputRef.current?.focus()
    }
  }, [showRejectInput])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black bg-opacity-60"
        onClick={onClose}
        aria-label="Close modal"
        tabIndex={-1}
      />
      <dialog
        open
        className="relative z-[1] bg-white rounded-2xl shadow-2xl w-full max-w-[40rem] overflow-hidden flex flex-col max-h-[90vh] border-0 p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">Extension Request</h3>
            <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${cfg.color}`}>
              {cfg.icon}
              {cfg.label}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100 outline-none border-0 bg-transparent"
            aria-label="Close"
          >
            <CloseIcon style={{ fontSize: '20px' }} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {/* Video preview */}
          <div className="px-6 pt-5">
          <div className="bg-gray-900 rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
            {renderVideoPreview()}
          </div>
          </div>

          {/* Details */}
          <div className="px-6 py-5 space-y-4">
            {/* Admin view: user info row */}
            {isAdmin && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 w-28 shrink-0 pt-0.5">Requested by</span>
                <span className="font-medium text-gray-800 break-all min-w-0">{req.user_email}</span>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 w-28 shrink-0">Target duration</span>
              <span
                className="px-2.5 py-0.5 text-xs font-semibold text-white rounded-full"
                style={{ background: 'linear-gradient(to right, #F69A7E, #939BEC)' }}
              >
                → {formatDuration(req.target_duration)}
              </span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 w-28 shrink-0">Submitted</span>
              <span className="text-gray-700">{timeAgo(req.created_at)}</span>
            </div>

            {req.reviewed_by && req.reviewed_at && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 w-28 shrink-0">Reviewed by</span>
                <span className="text-gray-700">{req.reviewed_by} · {timeAgo(req.reviewed_at)}</span>
              </div>
            )}

            {req.prompt && (
              <div className="text-sm">
                <p className="text-gray-500 mb-1">Prompt</p>
                <p className="text-gray-700 text-xs bg-gray-50 rounded-lg px-3 py-2 leading-relaxed">
                  {req.prompt}
                </p>
              </div>
            )}

            {req.status === 'rejected' && req.feedback && (
              <div className="text-sm">
                <p className="text-gray-500 mb-1">Rejection reason</p>
                <p className="text-red-600 text-xs bg-red-50 rounded-lg px-3 py-2">
                  {req.feedback}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-gray-100 flex flex-col gap-2">
          {/* View extended video */}
          {req.status === 'completed' && req.extended_video_id && (
            <button
              type="button"
              disabled={viewLoading}
              onClick={async () => {
                setViewLoading(true)
                try {
                  if (onViewExtended) {
                    await onViewExtended(req.extended_video_id!)
                  } else {
                    navigate(`/dashboard?video=${req.extended_video_id}`)
                  }
                } finally {
                  onClose()
                }
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow transition hover:opacity-90 disabled:opacity-80"
              style={{ background: 'linear-gradient(to right, #F69A7E, #939BEC)' }}
            >
              {viewLoading
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <PlayCircleIcon style={{ fontSize: '18px' }} />
              }
              {viewLoading ? 'Loading...' : 'View Extended Video'}
            </button>
          )}

          {/* Admin: approve / reject actions */}
          {isAdmin && req.status === 'pending' && (
            <>
              {showRejectInput && (
                <input
                  ref={rejectInputRef}
                  type="text"
                  placeholder="Rejection reason (optional)"
                  value={rejectFeedback}
                  onChange={e => setRejectFeedback(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300 bg-white"
                />
              )}
              <div className="flex gap-2">
                {showRejectInput ? (
                  <>
                    <button
                      type="button"
                      onClick={() => { setShowRejectInput(false); setRejectFeedback('') }}
                      className="flex-1 px-4 py-2.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => onReject?.(rejectFeedback)}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 transition disabled:opacity-50"
                    >
                      {actionLoading
                        ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <Cancel style={{ fontSize: '16px' }} />
                      }
                      Confirm Reject
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowRejectInput(true)}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-1 px-4 py-2.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-xl hover:bg-red-50 transition disabled:opacity-50"
                    >
                      <Cancel style={{ fontSize: '16px' }} />
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={onApprove}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition disabled:opacity-50 hover:opacity-90"
                      style={{ background: 'linear-gradient(to right, #F69A7E, #939BEC)' }}
                    >
                      {actionLoading
                        ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <CheckCircle style={{ fontSize: '16px' }} />
                      }
                      Approve
                    </button>
                  </>
                )}
              </div>
            </>
          )}

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
          >
            Close
          </button>
        </div>
      </dialog>
    </div>
  )
}

export default ExtensionRequestDetailModal
